/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  ReactFlow, 
  useNodesState, 
  useEdgesState, 
  useReactFlow, 
  Node, 
  Edge, 
  Handle, 
  Position,
  MarkerType,
  ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';
import { 
  Copy, 
  MoreVertical, 
  Cloud, 
  Filter
} from 'lucide-react';
import { CPR, SelectedEntity } from './lib/data';
import CloudInventoryPanel from './CloudInventoryPanel';

// BGP Neighbor Card Component
const NeighborNode: React.FC<{ data: any }> = ({ data }) => {
  const { neighborIp, prefixes } = data;
  
  // DEBUG: Log prefixes for this neighbor
  console.log(`NeighborNode ${neighborIp} rendering prefixes:`, prefixes.map((p: any) => p.prefix));
  
  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4 min-w-[300px]">
      <div 
        className="w-full h-1 mb-3 rounded-t-lg"
        style={{ backgroundColor: '#10B981' }}
      />
      
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-800">{neighborIp}</h3>
        <div className="flex items-center space-x-2">
          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
            Established
          </span>
          <button className="p-1 hover:bg-gray-100 rounded">
            <Copy size={14} className="text-gray-500" />
          </button>
          <button className="p-1 hover:bg-gray-100 rounded">
            <MoreVertical size={14} className="text-gray-500" />
          </button>
        </div>
      </div>
      
      <div className="text-sm text-gray-600 mb-3">
        <div>AS 65001 • updated 10:20</div>
        <div>Prefixes: {prefixes.length}</div>
      </div>
      
      <div className="space-y-2">
        {prefixes.map((prefixRow: any, index: number) => (
          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded relative">
            <div className="flex-1">
              <div className="font-medium text-sm">
                {prefixRow.prefix}
                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                  {prefixRow.family === 'ipv4-unicast' ? 'IPv4U' : 'VPNv4'}
                </span>
              </div>
              <div className="text-xs text-gray-500">
                LP {prefixRow.local_pref} • MED {prefixRow.med} • AS_PATH({prefixRow.as_path?.length || 0}) {prefixRow.as_path?.join(' ') || ''}
              </div>
              <div className="text-xs text-gray-400">
                → {prefixRow.vpcCount} VPCs • {prefixRow.rtCount} RTs
              </div>
            </div>
            <Handle
              type="source"
              position={Position.Right}
              id={`handle-peer-${neighborIp}-${prefixRow.prefix}-${prefixRow.family}`}
              className="w-1.5 h-1.5"
              style={{
                position: 'absolute',
                right: -6,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 6,
                height: 6,
                borderRadius: 9999,
                background: '#e8f0ff',
                opacity: 0.35,
                border: '1px solid #7aa7ff',
                zIndex: 50
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const VPCNode: React.FC<{ data: any }> = ({ data }) => {
  const { vpcId, routeTables } = data;
  
  return (
    <div 
      className="bg-white rounded-lg shadow-md border border-gray-200 p-4 min-w-[300px] vpcCard"
      style={{ zIndex: 2, pointerEvents: 'auto' }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <Cloud size={16} className="text-gray-500 mr-2" />
          <h3 className="font-semibold text-gray-800">{vpcId}</h3>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">AWS VPC</span>
        </div>
      </div>
      
      <div className="flex items-center space-x-2 mb-3">
        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">RTs {routeTables.length}</span>
        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">Prefixes {routeTables.reduce((sum: number, rt: any) => sum + rt.prefixes.length, 0)}</span>
        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">Neighbors {new Set(routeTables.flatMap((rt: any) => rt.prefixes.map((p: any) => p.neighbor_ip))).size}</span>
      </div>
      
      <div className="space-y-2">
        {routeTables.map((rt: any, index: number) => (
          <div 
            key={index} 
            className="flex items-center justify-between p-2 bg-gray-50 rounded relative"
            style={{ pointerEvents: 'auto', zIndex: 40 }}
          >
            <div className="flex items-center">
              <span className="ml-2 font-medium text-sm">
                {rt.route_table_id.length > 20 
                  ? `${rt.route_table_id.substring(0, 8)}...${rt.route_table_id.substring(rt.route_table_id.length - 8)}` 
                  : rt.route_table_id
                }
              </span>
            </div>
            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
              {rt.prefixes.length} prefixes
            </span>
            <Handle
              type="target"
              position={Position.Left}
              id={`handle-rt-${rt.route_table_id}`}
              className="rtHandle"
              style={{ 
                pointerEvents: 'auto', 
                zIndex: 10, 
                position: 'absolute', 
                left: -6,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 6,
                height: 6,
                borderRadius: 9999,
                background: '#e8f0ff',
                opacity: 0.35,
                border: '1px solid #7aa7ff',
                boxShadow: 'none'
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const CloudContainer: React.FC<{ data: any }> = ({ data }) => {
  const { label, vpcs, allData } = data;
  
  return (
    <div 
      className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-6 min-w-[400px] cloudCard"
      style={{ zIndex: 1, pointerEvents: 'none' }}
    >
      <div className="flex items-center mb-4">
        <Cloud size={20} className="text-gray-500 mr-2" />
        <h2 className="text-lg font-semibold text-gray-700">{label}</h2>
      </div>
      
      <div className="space-y-4">
        {vpcs.map((vpc: any, index: number) => {
          const routeTables = allData
            .filter((entry: any) => entry.vpc === vpc)
            .reduce((acc: any, entry: any) => {
              const existing = acc.find((rt: any) => rt.route_table_id === entry.route_table_id);
              if (existing) {
                existing.prefixes.push(entry);
              } else {
                acc.push({
                  route_table_id: entry.route_table_id,
                  prefixes: [entry]
                });
              }
              return acc;
            }, []);
          
          return (
            <VPCNode key={index} data={{ vpcId: vpc, routeTables }} />
          );
        })}
      </div>
    </div>
  );
};

// Node types
const nodeTypes = {
  neighbor: NeighborNode,
  vpc: VPCNode,
  cloud: CloudContainer,
};

// Add CSS for RT handles
const rtHandleStyles = `
  .rtHandle:hover {
    opacity: 0.85 !important;
  }
  .react-flow__edges {
    z-index: 4;
  }

  @keyframes flowPink {
    0% { stroke-dashoffset: 0; }
    100% { stroke-dashoffset: -100; }
  }
`;

const BgpVisibilityPage: React.FC = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { fitView } = useReactFlow();
  
  // State
  const [selectedEntity, setSelectedEntity] = useState<SelectedEntity | null>(null);
  const [currentCpr, setCurrentCpr] = useState<CPR | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Direction and Family controls
  const [direction, setDirection] = useState<'INBOUND' | 'OUTBOUND'>('INBOUND');
  const [showIPv4U, setShowIPv4U] = useState(true);
  const [showVPNv4, setShowVPNv4] = useState(false);
  const [bestOnly, setBestOnly] = useState(false);

  // URL state management
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cloud = params.get('cloud');
    const account_id = params.get('account');
    const region = params.get('region');
    const type = params.get('type');
    const id = params.get('id');
    
    if (cloud && account_id && region && type && id) {
      setSelectedEntity({
        cloud,
        account_id,
        account_display_name: account_id, // Default fallback
        region,
        type: type as 'reflector' | 'cluster',
        id,
        display_name: id
      });
    }
  }, []);

  useEffect(() => {
    if (selectedEntity) {
      const params = new URLSearchParams();
      params.set('cloud', selectedEntity.cloud);
      params.set('account', selectedEntity.account_id);
      params.set('region', selectedEntity.region);
      params.set('type', selectedEntity.type);
      params.set('id', selectedEntity.id);
      
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [selectedEntity]);

  // Load CPR data when entity is selected
  useEffect(() => {
    const loadCprData = async () => {
      if (!selectedEntity) {
        setCurrentCpr(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const cprPath = selectedEntity.type === 'reflector'
          ? `/data/cpr/reflectors/cpr_${selectedEntity.id}.json`
          : `/data/cpr/clusters/cpr_${selectedEntity.id}.json`;
        const response = await fetch(cprPath);

        if (!response.ok) {
          throw new Error(`No CPR data found for ${selectedEntity.display_name}`);
        }

        const cprData = await response.json();
        setCurrentCpr(cprData);
      } catch (err) {
        console.error('Failed to load CPR data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load CPR data');
        setCurrentCpr(null);
      } finally {
        setLoading(false);
      }
    };

    loadCprData();
  }, [selectedEntity]);

  // Generate graph from CPR data
  const generateGraph = useCallback(() => {
    if (!currentCpr) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];

    // 1. Filtrado inicial
    const set = direction === 'INBOUND' ? currentCpr.INBOUND : currentCpr.OUTBOUND;
    const rows = [
      ...(showIPv4U ? (set.ipv4_unicast_cpr || []) : []),
      ...(showVPNv4 && direction === 'INBOUND' ? ((set as any).vpnv4_cpr || []) : []),
    ];

    console.log('Raw rows:', rows.length);

    // 2. Claves canónicas
    const rtKey = (rtb: string, prefix: string, fam: string) => `${rtb}|${prefix}|${fam}`;

    // 3. Agrupa por destino (RT + prefijo + familia)
    const groups = new Map<string, any>();
    for (const r of rows) {
      const fam = (r.family || 'ipv4-unicast').toLowerCase();
      const k = rtKey(r.route_table_id, r.prefix, fam);
      const g = groups.get(k) ?? { records: [], peers: new Set(), cands: new Map() };
      g.records.push(r);
      g.peers.add(r.neighbor_ip);
      for (const c of (r.candidates || [])) {
        g.peers.add(c.neighbor_ip);
        g.cands.set(c.neighbor_ip, c);
      }
      groups.set(k, g);
    }

    console.log('Groups:', groups.size);

    // 4. Elige 1 ganador por (prefix, RT)
    function tieBreak(a: any, b: any) {
      if (a.local_pref !== b.local_pref) return b.local_pref - a.local_pref;
      if (a.med !== b.med) return a.med - b.med;
      return (a.as_path?.length || 0) - (b.as_path?.length || 0);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function pickWinner(g: any) {
      // 1) candidatos con selected:true
      const selected = [...g.cands.values()].filter((c: any) => c.selected);
      if (selected.length) return selected.sort(tieBreak)[0].neighbor_ip;
      // 2) usa el propio neighbor del registro mejor posicionado
      const all = g.records.map((r: any) => ({
        neighbor_ip: r.neighbor_ip,
        local_pref: r.local_pref,
        med: r.med,
        as_path: r.as_path
      }));
      if (all.length) return all.sort(tieBreak)[0].neighbor_ip;
      // 3) fallback: primer peer
      return [...g.peers][0];
    }

    // 5. Crear datos para nodos - NUEVA LÓGICA PARA TODOS LOS PREFIJOS
    const neighborPrefixes = new Map<string, any[]>();
    
    // Primero, obtener todos los peers únicos de TODOS los records y candidatos
    const allPeers = new Set<string>();
    rows.forEach(record => {
      allPeers.add(record.neighbor_ip);
      if (record.candidates) {
        record.candidates.forEach((candidate: any) => {
          allPeers.add(candidate.neighbor_ip);
        });
      }
    });
    
    console.log('All peers found:', Array.from(allPeers));
    
    // Para cada peer, encontrar TODOS los prefijos donde aparece (como principal o candidato)
    Array.from(allPeers).forEach(peer => {
      if (!neighborPrefixes.has(peer)) {
        neighborPrefixes.set(peer, []);
      }
      
      // Buscar prefijos donde este peer es el principal O candidato
      rows.forEach(record => {
        const { prefix, family } = record;
        const fam = (family || 'ipv4-unicast').toLowerCase();
        
        // Verificar si este peer ya tiene este prefijo
        const existingPrefix = neighborPrefixes.get(peer)!.find(p => 
          p.prefix === prefix && p.family === fam
        );
        
        if (!existingPrefix) {
          let peerData = null;
          
          // Si es el peer principal del record
          if (record.neighbor_ip === peer) {
            peerData = {
              local_pref: record.local_pref,
              med: record.med,
              as_path: record.as_path
            };
          }
          // Si es un candidato
          else if (record.candidates) {
            const candidateData = record.candidates.find((c: any) => c.neighbor_ip === peer);
            if (candidateData) {
              peerData = {
                local_pref: candidateData.local_pref,
                med: candidateData.med,
                as_path: candidateData.as_path
              };
            }
          }
          
          // Solo agregar si encontramos datos para este peer
          if (peerData) {
            // Contar VPCs y RTs únicos para este prefijo desde este peer
            const relatedRecords = rows.filter(r => 
              r.prefix === prefix && 
              (r.neighbor_ip === peer || r.candidates?.some((c: any) => c.neighbor_ip === peer))
            );
            
            const vpcCount = new Set(relatedRecords.map(r => r.vpc)).size;
            const rtCount = relatedRecords.length;
            
            neighborPrefixes.get(peer)!.push({
              neighbor_ip: peer,
              prefix: prefix,
              family: fam,
              local_pref: peerData.local_pref || 0,
              med: peerData.med || 0,
              as_path: peerData.as_path || [],
              vpcCount: vpcCount,
              rtCount: rtCount
            });
          }
        }
      });
    });

    console.log('Neighbor prefixes:', neighborPrefixes);

    // Crear nodos de neighbors
    const uniqueNeighbors = Array.from(neighborPrefixes.keys());
    const uniqueVPCs = Array.from(new Set(rows.map(entry => entry.vpc)));

    // Layout calculation - altura dinámica basada en contenido
    const baseCardHeight = 200; // Altura base del card (header + metadata)
    const prefixRowHeight = 65; // Altura aproximada por fila de prefijo
    const cardPadding = 60; // Espaciado entre cards (reducido 50%)
    
    let currentY = 50; // Empezar desde arriba
    
    uniqueNeighbors.forEach((neighbor) => {
      const prefixes = neighborPrefixes.get(neighbor) || [];
      
      newNodes.push({
        id: `neighbor-${neighbor}`,
        type: 'neighbor',
        position: { x: 50, y: currentY },
        data: { neighborIp: neighbor, prefixes: prefixes },
      });
      
      // Calcular altura dinámica para este card
      const cardHeight = baseCardHeight + (prefixes.length * prefixRowHeight);
      currentY += cardHeight + cardPadding; // Mover Y para el siguiente card
    });

    // Crear nodo Cloud - alineado con el primer neighbor
    if (uniqueVPCs.length > 0) {
      const cloudY = 50; // Mismo nivel que el primer neighbor
      newNodes.push({
        id: 'cloud-container',
        type: 'cloud',
        position: { x: 600, y: cloudY },
        data: { label: 'Cloud (AWS)', vpcs: uniqueVPCs, allData: rows },
      });
    }

    // 6. Aristas - NUEVA LÓGICA DIRECTA (procesar todas las filas)
    const edgeIds = new Set<string>(); // Para prevenir duplicados
    
    // Procesar TODAS las filas individuales (NO usar agrupación buggy)
    rows.forEach((record) => {
      const { neighbor_ip, prefix, route_table_id, vpc, family, candidates } = record;
      
      // 1. Edge BEST desde el neighbor principal
      const bestEdgeId = `${direction.toLowerCase()}-${(family||'ipv4-unicast')}-${prefix}-${neighbor_ip}-${route_table_id}-best`;
      
      if (!edgeIds.has(bestEdgeId)) {
        edgeIds.add(bestEdgeId);
        
        newEdges.push({
          id: bestEdgeId,
          source: `neighbor-${neighbor_ip}`,
          sourceHandle: `handle-peer-${neighbor_ip}-${prefix}-${family}`,
          target: 'cloud-container',
          targetHandle: `handle-rt-${route_table_id}`,
          type: 'default',
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 7,
            height: 7,
            color: '#ff4088',
          },
          style: {
            stroke: '#ff4088',
            strokeWidth: 1.6,
            strokeDasharray: '10 6',
            animation: 'flowPink 1.0s linear infinite',
            filter: 'drop-shadow(0 0 8px rgba(255, 64, 136, 0.85))',
            opacity: 1,
            zIndex: 5,
          },
          data: {
            label: prefix,
            family: family,
            selected: true,
            neighbor_ip: neighbor_ip,
            vpc: vpc,
            route_table_id: route_table_id,
          },
        });
      }
      
      // 2. Edges ALTERNATIVAS desde candidatos (solo si no es "Best only")
      if (!bestOnly && candidates && Array.isArray(candidates)) {
        candidates
          .filter(candidate => candidate.selected === false)
          .forEach(candidate => {
            const altEdgeId = `${direction.toLowerCase()}-${(family||'ipv4-unicast')}-${prefix}-${candidate.neighbor_ip}-${route_table_id}-alt`;
            
            if (!edgeIds.has(altEdgeId)) {
              edgeIds.add(altEdgeId);
              
              newEdges.push({
                id: altEdgeId,
                source: `neighbor-${candidate.neighbor_ip}`,
                sourceHandle: `handle-peer-${candidate.neighbor_ip}-${prefix}-${family}`,
                target: 'cloud-container',
                targetHandle: `handle-rt-${route_table_id}`,
                type: 'default',
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  width: 6,
                  height: 6,
                  color: '#9CA3AF',
                },
                style: {
                  stroke: '#9CA3AF',
                  strokeWidth: 1.4,
                  strokeDasharray: '6 4',
                  opacity: 0.55,
                  zIndex: 6,
                },
                data: {
                  label: prefix,
                  family: family,
                  selected: false,
                  neighbor_ip: candidate.neighbor_ip,
                  vpc: vpc,
                  route_table_id: route_table_id,
                },
              });
            }
          });
      }
    });

    console.log('Edges created successfully:', newEdges.length, 'ALT edges:', newEdges.filter(e => e.id.includes('alt')).length);

    // Fin de la nueva lógica directa

    console.log('Final nodes:', newNodes.length, 'Final edges:', newEdges.length);

    setNodes(newNodes);
    setEdges(newEdges);

    setTimeout(() => {
      fitView({ 
        padding: 0.1,
        includeHiddenNodes: false,
        minZoom: 0.5,
        maxZoom: 1.5 
      });
    }, 200);
  }, [currentCpr, direction, showIPv4U, showVPNv4, bestOnly, fitView]);
  
  useEffect(() => {
    generateGraph();
  }, [generateGraph]);

  // Calculate metrics for toolbar based on current filters
  const getFilteredData = () => {
    if (!currentCpr) return [];
    const set = direction === 'INBOUND' ? currentCpr.INBOUND : currentCpr.OUTBOUND;
    const records = [
      ...(showIPv4U ? (set.ipv4_unicast_cpr || []) : []),
      ...(showVPNv4 && direction === 'INBOUND' ? ((set as any).vpnv4_cpr || []) : []),
    ];
    return bestOnly 
      ? records.filter(entry => !entry.candidates || entry.candidates.some((c: any) => c.selected && c.neighbor_ip === entry.neighbor_ip))
      : records;
  };
  
  const allEntries = getFilteredData();
  const metricsNeighbors = Array.from(new Set(allEntries.map(entry => entry.neighbor_ip)));
  const metricsVPCs = Array.from(new Set(allEntries.map(entry => entry.vpc)));
  const metricsRTs = Array.from(new Set(allEntries.map(entry => entry.route_table_id)));
  const metricsPrefixes = Array.from(new Set(allEntries.map(entry => `${entry.prefix}|${entry.family}`)));

  return (
    <>
      <style>{rtHandleStyles}</style>
      <div 
        className="grid gap-4 min-h-screen"
        style={{ gridTemplateColumns: '340px 1fr' }}
      >
      {/* Cloud Inventory Panel */}
      <div 
        className="bg-white border-r border-gray-200"
        style={{ 
          position: 'sticky',
          top: '0',
          height: '100vh',
          maxHeight: '100vh',
          overflow: 'hidden'
        }}
      >
        <CloudInventoryPanel onEntitySelect={setSelectedEntity} />
      </div>

      {/* Main Graph Area */}
      <div className="flex flex-col">
        {/* Canvas Controls - ALWAYS visible */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 sticky z-10" style={{ top: '0' }}>
          <div className="flex items-center justify-between">
            {/* Left Controls */}
            <div className="flex items-center space-x-4">
              {/* Direction Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button 
                  onClick={() => setDirection('INBOUND')}
                  className={`px-3 py-1 text-sm font-medium rounded-md ${
                    direction === 'INBOUND' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Inbound
                </button>
                <button 
                  onClick={() => setDirection('OUTBOUND')}
                  className={`px-3 py-1 text-sm font-medium rounded-md ${
                    direction === 'OUTBOUND' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Outbound
                </button>
              </div>

              {/* Address Family Chips */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowIPv4U(!showIPv4U)}
                  className={`px-3 py-1 text-sm font-medium rounded-md border ${
                    showIPv4U 
                      ? 'bg-blue-50 border-blue-200 text-blue-800' 
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  IPv4 Unicast
                </button>
                <button
                  onClick={() => direction === 'INBOUND' && setShowVPNv4(!showVPNv4)}
                  disabled={direction === 'OUTBOUND'}
                  className={`px-3 py-1 text-sm font-medium rounded-md border ${
                    showVPNv4 && direction === 'INBOUND'
                      ? 'bg-purple-50 border-purple-200 text-purple-800' 
                      : direction === 'OUTBOUND'
                      ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}
                  title={direction === 'OUTBOUND' ? 'VPNv4 only available for Inbound' : ''}
                >
                  VPNv4
                </button>
              </div>
              
              {/* Best Only Toggle */}
              <label className="flex items-center space-x-2">
                <input 
                  type="checkbox" 
                  className="rounded"
                  checked={bestOnly}
                  onChange={(e) => setBestOnly(e.target.checked)}
                />
                <span className="text-sm font-medium text-gray-700">Best only</span>
              </label>
              
              {/* Filters Button */}
              <button className="flex items-center space-x-1 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">
                <Filter size={14} />
                <span>Filters</span>
                <span className="bg-gray-300 text-gray-600 text-xs px-1.5 py-0.5 rounded-full">0</span>
              </button>
            </div>
            
            {/* Right Metrics */}
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>{currentCpr ? metricsNeighbors.length : '—'} Neighbors</span>
              <span>•</span>
              <span>{currentCpr ? metricsVPCs.length : '—'} VPCs</span>
              <span>•</span>
              <span>{currentCpr ? metricsRTs.length : '—'} RTs</span>
              <span>•</span>
              <span>{currentCpr ? metricsPrefixes.length : '—'} Prefixes</span>
              <button className="ml-4 p-1 hover:bg-gray-100 rounded">
                <MoreVertical size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Graph Content */}
        <div className="flex-1 p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                <p className="text-gray-600">Loading CPR data...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-8 bg-red-50 border border-red-200 rounded-lg shadow-sm">
                <p className="text-red-600 font-semibold mb-2">Error loading data</p>
                <p className="text-red-500 text-sm">{error}</p>
              </div>
            </div>
        ) : !currentCpr ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8 bg-gray-50 border border-gray-200 rounded-lg shadow-sm">
              <p className="text-gray-600 font-semibold mb-2">No device selected</p>
              <p className="text-gray-500 text-sm">Select any Reflector or Cluster from the Cloud Inventory</p>
            </div>
          </div>
        ) : allEntries.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8 bg-yellow-50 border border-yellow-200 rounded-lg shadow-sm">
              <p className="text-yellow-800 font-semibold mb-2">No routes for current direction</p>
              <p className="text-yellow-600 text-sm">
                No {direction.toLowerCase()} routes found for the selected filters
              </p>
            </div>
          </div>
        ) : (
            <div className="h-full relative">
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                nodesDraggable={false}
                elementsSelectable={false}
                nodesConnectable={false}
                panOnDrag={true}
                zoomOnScroll={true}
                zoomOnPinch={true}
                  fitView
                  fitViewOptions={{ padding: 0.15, includeHiddenNodes: false, minZoom: 0.2, maxZoom: 2.0 }}
                defaultEdgeOptions={{ type: 'default' }}
              >
                {/* Legend */}
                <div className="absolute bottom-4 right-4 bg-white border border-gray-200 rounded-lg p-3 shadow-sm z-20">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Legend</h4>
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-0.5 bg-pink-500" style={{ 
                        backgroundImage: 'repeating-linear-gradient(to right, #ff4088 0px, #ff4088 6px, transparent 6px, transparent 10px)',
                        animation: 'dash 0.6s linear infinite'
                      }}></div>
                      <span className="text-xs text-gray-600">Best Path</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-0.5 bg-gray-400" style={{ 
                        backgroundImage: 'repeating-linear-gradient(to right, #9CA3AF 0px, #9CA3AF 4px, transparent 4px, transparent 6px)'
                      }}></div>
                      <span className="text-xs text-gray-600">Alternate Path</span>
                    </div>
                  </div>
                </div>
              </ReactFlow>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

const BgpVisibilityPageWrapper: React.FC = () => {
  return (
    <ReactFlowProvider>
      <BgpVisibilityPage />
    </ReactFlowProvider>
  );
};

export default BgpVisibilityPageWrapper;